<template>
  <div
    v-if="segment.total"
    class="audience-segment-card"
    :class="{ small: small }"
    @click="selectSegmentType()">
    <div class="icon">
      <component :is="segment.icon" />
    </div>
    <div class="name">
      <span
        v-tooltip="capitalize(segment.name)"
        class="title"
        :title="capitalize(segment.name)">
        {{ capitalize(segment.name) }}
      </span>

      <span
        v-if="!small"
        v-tooltip.left="tooltipContent"
        class="total">
        {{ formatNumber(segment.total) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import ExternalSegmentType from '@/common/models/audience/ExternalSegmentType';
  import { formatNumber } from '@/globals';

  interface Props {
    segment: ExternalSegmentType;
    small?: boolean;
  }
  interface Emits {
    (e: 'segmentTypeSelected', segment: ExternalSegmentType): void;
  }
  const props = defineProps<Props>();
  const emits = defineEmits<Emits>();
  const i18n = useI18n();

  const tooltipContent = computed(() =>
    capitalize(i18n.t('names.availablePredefinedSegmentInCategory', props.segment.total > 1 ? 2 : 1)),
  );

  function selectSegmentType() {
    emits('segmentTypeSelected', props.segment);
  }
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  .audience-segment-card {
    display: flex;
    flex-direction: column;
    box-shadow: $shadow;
    padding: 2rem;
    position: relative;
    border: 2px solid transparent;
    transition: all 250ms;
    border-radius: 2px;
    cursor: pointer;

    &:hover {
      transition: all 500ms;
      border: 2px solid $lift-blue;
    }

    &.small {
      flex-direction: row;
      padding: 1rem;
      align-items: center;

      .icon {
        height: 8rem;
      }

      .description {
        display: none;
      }

      .arrow svg {
        height: 4rem;
      }

      .name {
        border-bottom: none !important;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    .icon,
    .name,
    .description {
      display: flex;
    }

    .icon,
    .name {
      margin-bottom: 1rem;
    }

    .icon {
      height: 15rem;
      justify-content: center;
    }

    .name {
      font-size: 2rem;
      font-weight: bold;
      align-items: baseline;
      justify-content: space-between;

      border-bottom: 1px solid var(--iconColorLight);

      span.title {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      span.total {
        background: var(--iconColorLight);

        padding: 0.25rem 1rem;
        border-radius: 4px 4px 0 0;
        font-size: 1.25rem;
        display: flex;
        align-items: center;
      }
    }
  }
</style>
